import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDownUp, CheckCircle2, ChevronDown, ExternalLink, RefreshCw, Search, Settings, Wallet, X } from 'lucide-react'
import { Connection, VersionedTransaction } from '@solana/web3.js'
import { get, post, sleep, short } from '../../api/client'
import { useRoseWallet } from '../../wallet/useRoseWallet'
import { ensureVerifiedSolana } from '../../wallet/verify'

const SOL = { address: 'So11111111111111111111111111111111111111112', symbol: 'SOL', name: 'Solana', decimals: 9, verified: true }
const USDC = { address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', symbol: 'USDC', name: 'USD Coin', decimals: 6, verified: true }
const ROSE = { address: 'RoSeiVjW5H48ucPAJh1LJGBBzPpqvsokfDGpgHXDtdF', symbol: 'ROSE', name: 'OnlyRose', decimals: 6, verified: true }
const RPC = import.meta.env.VITE_SOLANA_RPC_URL || 'https://api.mainnet-beta.solana.com'

const normalizeAmount = value => String(value || '').trim().replace(/^\./, '0.')
const toAtomic = (value, decimals) => {
  const raw = normalizeAmount(value)
  if (!/^\d+(\.\d+)?$/.test(raw)) throw new Error('Enter a valid amount')
  const [whole, fraction = ''] = raw.split('.')
  const padded = (fraction + '0'.repeat(decimals)).slice(0, decimals)
  return (BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt(padded || '0')).toString()
}
const fromAtomic = (value, decimals, max = 9) => {
  if (value == null || value === '') return ''
  const n = BigInt(value)
  const base = 10n ** BigInt(decimals)
  const whole = n / base
  const frac = (n % base).toString().padStart(decimals, '0').slice(0, max).replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : `${whole}`
}
const decode = base64 => Uint8Array.from(atob(base64), c => c.charCodeAt(0))
const signatureOf = value => typeof value === 'string' ? value : value?.signature || value?.txid || ''
const formatUsd = value => Number(value) > 0 ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'
const solanoHas = (provider, key) => provider && typeof provider[key] === 'function'

function TokenLogo({ token }) {
  const [failed, setFailed] = useState(false)
  const src = token?.logoURI || token?.logoUri
  return src && !failed
    ? <img className="trade-token-logo" src={src} alt="" onError={() => setFailed(true)} />
    : <i className="trade-token-fallback">{token?.symbol?.[0] || '?'}</i>
}

function TokenIdentity({ token }) {
  return <span className="token-identity">
    <TokenLogo token={token} />
    <span><b>{token?.symbol || 'Select'}</b><small>{token?.name || ''}</small></span>
    {token?.verified && <CheckCircle2 />}
  </span>
}

function QuoteRow({ label, value, positive }) {
  return <div className="quote-row"><span>{label}</span><b className={positive ? 'positive' : ''}>{value}</b></div>
}

function TokenDetailRow({ label, token, price }) {
  return <div className="token-detail-row">
    <span>{label}</span>
    <TokenIdentity token={token} />
    <code>{price ? `${formatUsd(price)} · ` : ''}{short(token?.address || '')}</code>
  </div>
}

function TokenModal({ selected, onSelect, onClose }) {
  const [query, setQuery] = useState('')
  const [tokens, setTokens] = useState([SOL, USDC, ROSE])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setBusy(true)
      setError('')
      try {
        const suffix = query.trim() ? `&query=${encodeURIComponent(query.trim())}` : ''
        const result = await get(`/api/v1/trade/jupiter/tokens?limit=100${suffix}`, { signal: controller.signal })
        setTokens(result.tokens?.length ? result.tokens : [SOL, USDC, ROSE])
      } catch (err) {
        if (err.name !== 'AbortError') setError('Rose token index is temporarily unavailable; showing the local safety list.')
      } finally {
        if (!controller.signal.aborted) setBusy(false)
      }
    }, query ? 350 : 0)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query])

  return <div className="trade-modal-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <div className="trade-token-modal panel">
      <div className="token-modal-head">
        <div><span>Rose token search</span><h3>{selected ? 'Select token' : 'Select token'}</h3></div>
        <button onClick={onClose}><X /></button>
      </div>
      <label className="token-search"><Search /><input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search symbol/name or paste mint" />{busy && <RefreshCw className="spin" />}</label>
      <div className="token-source"><span>Rose indexed tokens</span><small>{tokens.length} results</small></div>
      {error && <div className="trade-error">{error}</div>}
      <div className="token-result-list">
        {tokens.map(token => <button key={token.address || token.id} className={(token.address || token.id) === selected?.address ? 'selected' : ''} onClick={() => onSelect({ ...token, address: token.address || token.id })}>
          <TokenIdentity token={token} />
          <span className="token-result-meta"><b>{token.balance ? `${token.balance} ${token.symbol}` : ''}</b><small>{short(token.address || token.id)}</small></span>
          {(token.address || token.id) === selected?.address && <CheckCircle2 />}
        </button>)}
        {!busy && !tokens.length && <div className="trade-empty">No matching token found for Solana.</div>}
      </div>
    </div>
  </div>
}

function SwapSuccess({ record, onClose, onRefresh }) {
  if (!record) return null
  const status = String(record.providerStatus || record.status || record.roseStatus || 'PENDING').toUpperCase()
  const confirmed = ['CONFIRMED', 'FINALIZED', 'COMPLETE', 'COMPLETED', 'SUCCESS'].some(value => status.includes(value))
  const sig = record.signature || record.sourceTxHash || record.source_tx_hash
  return <div className="rose-swap-status-overlay" role="dialog" aria-modal="true" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <div className="rose-swap-status-modal">
      <button className="rose-swap-status-close" onClick={onClose}><X /></button>
      <div className={`rose-swap-status-icon ${confirmed ? 'success' : ''}`}><CheckCircle2 /></div>
      <span className="eyebrow">{confirmed ? 'CONFIRMED' : 'SWAP STATUS'}</span>
      <h2>{confirmed ? 'Swap successful' : 'Swap submitted'}</h2>
      <p>{confirmed ? 'Solana confirmed the transaction · CONFIRMED' : `Current status · ${status}`}</p>
      {sig && <a className="primary" href={`https://solscan.io/tx/${sig}`} target="_blank" rel="noreferrer">View transaction on Solscan <ExternalLink size={15} /></a>}
      {!confirmed && <button className="secondary" onClick={onRefresh}><RefreshCw />Refresh status</button>}
    </div>
  </div>
}

function TokenBox({ label, token, amount, setAmount, balance, onToken, output, onMax }) {
  return <div className={`token-box ${output ? 'output' : ''}`}>
    <div><span>{label}</span><small>{balance || '0'} {token?.symbol || ''}{onMax && <button className="max-link" onClick={onMax}>MAX</button>}</small></div>
    <div className="token-row">
      <input inputMode="decimal" value={amount || ''} readOnly={output} onChange={e => setAmount?.(e.target.value)} placeholder="0.00" />
      <button onClick={onToken}><TokenLogo token={token} /><span>{token?.symbol || 'Select'}</span><ChevronDown /></button>
    </div>
  </div>
}

export default function SwapPage() {
  const { solana, solanaProvider, open } = useRoseWallet()
  const [providers, setProviders] = useState(null)
  const [input, setInput] = useState(SOL)
  const [output, setOutput] = useState(ROSE)
  const [balances, setBalances] = useState({})
  const [prices, setPrices] = useState({})
  const [amount, setAmount] = useState('')
  const [slippage, setSlippage] = useState(50)
  const [preview, setPreview] = useState(null)
  const [working, setWorking] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [message, setMessage] = useState('')
  const [selector, setSelector] = useState(null)
  const [record, setRecord] = useState(null)
  const abort = useRef(null)

  const loadBalances = async () => {
    if (!solana.address) { setBalances({}); return }
    try {
      const result = await get(`/api/v1/trade/solana/balances?address=${encodeURIComponent(solana.address)}`)
      setBalances(result.balances || {})
    } catch (err) { setMessage(err.message) }
  }

  useEffect(() => {
    Promise.allSettled([get('/api/v1/trade/providers'), get('/api/v1/trade/defaults')]).then(([providerResult, defaultsResult]) => {
      if (providerResult.status === 'fulfilled') setProviders(providerResult.value)
      if (defaultsResult.status === 'fulfilled' && defaultsResult.value?.swapDestination?.address && Number.isInteger(Number(defaultsResult.value.swapDestination.decimals))) {
        setOutput(defaultsResult.value.swapDestination)
      }
    })
  }, [])

  useEffect(() => { loadBalances() }, [solana.address])

  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(() => {
      get(`/api/v1/trade/jupiter/prices?mints=${encodeURIComponent(`${input.address},${output.address}`)}`, { signal: controller.signal })
        .then(result => setPrices(result.prices || {}))
        .catch(() => setPrices({}))
    }, 250)
    return () => { clearTimeout(timer); controller.abort() }
  }, [input.address, output.address])

  useEffect(() => {
    abort.current?.abort()
    setPreview(null)
    const raw = normalizeAmount(amount)
    if (!raw || Number(raw) <= 0 || input.address === output.address) return
    const controller = new AbortController()
    abort.current = controller
    const timer = setTimeout(async () => {
      setPreviewing(true)
      try {
        const body = { input_mint: input.address, output_mint: output.address, amount: toAtomic(raw, Number(input.decimals)), slippage_bps: Number(slippage) }
        const result = await post('/api/v1/trade/jupiter/preview', body, { signal: controller.signal })
        setPreview(result)
        setMessage('')
      } catch (err) {
        if (err.name !== 'AbortError') { setPreview(null); setMessage(err.message) }
      } finally { if (!controller.signal.aborted) setPreviewing(false) }
    }, 350)
    return () => { clearTimeout(timer); controller.abort() }
  }, [input.address, output.address, amount, slippage])

  const inputBalance = balances[input.address]?.uiAmountString || '0'
  const outputBalance = balances[output.address]?.uiAmountString || '0'
  const receive = preview?.outAmount ? fromAtomic(preview.outAmount, Number(output.decimals), 9) : ''
  const minimum = useMemo(() => {
    if (!preview?.outAmount) return '—'
    const out = BigInt(preview.outAmount)
    return `${fromAtomic((out - out * BigInt(slippage) / 10000n).toString(), Number(output.decimals), 9)} ${output.symbol}`
  }, [preview?.outAmount, slippage, output])
  const rate = useMemo(() => {
    const pay = Number(amount)
    const received = Number(receive)
    return pay > 0 && received > 0 ? `1 ${input.symbol} = ${(received / pay).toLocaleString(undefined, { maximumFractionDigits: 8 })} ${output.symbol}` : '—'
  }, [amount, receive, input, output])

  const swapDirection = () => {
    setInput(output)
    setOutput(input)
    if (receive) setAmount(receive)
    setPreview(null)
  }
  const choose = (which, token) => {
    if (which === 'input') {
      if (token.address === output.address) setOutput(input)
      setInput(token)
    } else {
      if (token.address === input.address) setInput(output)
      setOutput(token)
    }
    setSelector(null)
    setPreview(null)
  }
  const refreshStatus = async () => {
    if (!record?.id) return
    setWorking(true)
    try {
      const result = await post(`/api/v1/trade/jupiter/${record.id}/refresh`)
      setRecord({ ...record, ...result })
      if (['complete', 'confirmed'].includes(String(result.status).toLowerCase())) await loadBalances()
    } catch (err) { setMessage(err.message) } finally { setWorking(false) }
  }
  const execute = async () => {
    setWorking(true)
    setMessage('Preparing the swap for wallet review…')
    try {
      if (!solana.address) throw new Error('Connect a Solana wallet before swapping')
      if (!solanaProvider) throw new Error('Solana wallet provider unavailable')
      await ensureVerifiedSolana(solana.address, solanaProvider)
      const order = await post('/api/v1/trade/jupiter/order', { input_mint: input.address, output_mint: output.address, amount: toAtomic(amount, Number(input.decimals)), slippage_bps: Number(slippage), taker: solana.address })
      setPreview(order)
      if (!order.transaction) throw new Error('The swap could not be prepared for signing')
      const tx = VersionedTransaction.deserialize(decode(order.transaction))
      const connection = new Connection(RPC, 'confirmed')
      let signature = ''
      if (solanoHas(solanaProvider, 'signAndSendTransaction')) signature = signatureOf(await solanaProvider.signAndSendTransaction(tx, []))
      else if (solanoHas(solanaProvider, 'sendTransaction')) signature = signatureOf(await solanaProvider.sendTransaction(tx, connection))
      else if (solanoHas(solanaProvider, 'signTransaction')) {
        const signed = await solanaProvider.signTransaction(tx)
        signature = await connection.sendRawTransaction(signed.serialize(), { skipPreflight: false, maxRetries: 5 })
      } else if (solanoHas(solanaProvider, 'signAllTransactions')) {
        const [signed] = await solanaProvider.signAllTransactions([tx])
        signature = await connection.sendRawTransaction(signed.serialize(), { skipPreflight: false, maxRetries: 5 })
      } else throw new Error('Connected Solana wallet cannot sign and send this transaction')
      if (!signature) throw new Error('Solana wallet did not return a transaction signature')
      setMessage(`Swap broadcast · ${short(signature)}`)
      let submitted = await post('/api/v1/trade/jupiter/submit', { quote_id: order.id, source_tx_hash: signature })
      submitted = { ...submitted, signature }
      setRecord(submitted)
      setMessage('Swap submitted. Waiting for Solana confirmation…')
      for (let i = 0; i < 18; i += 1) {
        await sleep(1800)
        const next = await post(`/api/v1/trade/jupiter/${submitted.id}/refresh`).catch(() => null)
        if (!next) continue
        submitted = { ...submitted, ...next, signature }
        setRecord(submitted)
        const state = String(next.status || next.providerStatus || '').toLowerCase()
        if (['complete', 'confirmed', 'finalized'].some(value => state.includes(value))) {
          setMessage('Swap confirmed')
          await loadBalances()
          break
        }
        if (['failed', 'expired', 'cancelled'].some(value => state.includes(value))) break
      }
    } catch (err) { setMessage(err.message) } finally { setWorking(false) }
  }

  const execution = Boolean(providers?.providers?.jupiter?.execution)

  return <div className="rose-trade-page compact-trade-page">
    <div className="trade-compact-head">
      <div><div className="eyebrow"><Wallet size={14} />Rose Trade</div><h1>Swap on Solana</h1><p>Choose two assets, enter an amount and review the live result before signing.</p></div>
      <details className="trade-settings-menu">
        <summary title="Swap settings"><Settings /></summary>
        <div className="panel"><b>Swap settings</b><label><span>Slippage</span><div>{[10, 50, 100].map(value => <button key={value} className={slippage === value ? 'active' : ''} onClick={() => setSlippage(value)}>{value / 100}%</button>)}<input type="number" min="1" max="500" value={slippage} onChange={e => setSlippage(Math.max(1, Math.min(500, Number(e.target.value) || 1)))} /><em>bps</em></div></label><button className="secondary full" onClick={loadBalances}><RefreshCw />Refresh balances</button></div>
      </details>
    </div>

    <div className="compact-trade-shell">
      <section className="swap-card panel compact-swap-card">
        <TokenBox label="You pay" amount={amount} setAmount={setAmount} token={input} balance={inputBalance} onToken={() => setSelector('input')} onMax={() => setAmount(inputBalance)} />
        <button className="flip-btn" onClick={swapDirection} aria-label="Swap token direction"><ArrowDownUp /></button>
        <TokenBox label="You receive" amount={receive} token={output} balance={outputBalance} output onToken={() => setSelector('output')} />

        <div className="compact-quote-line">
          <span>{previewing ? <><RefreshCw className="spin" />Updating quote</> : preview ? <><CheckCircle2 />Quote ready</> : <>Enter an amount</>}</span>
          {preview && <b>{minimum} minimum</b>}
        </div>

        {execution
          ? <button className="primary full trade-submit" disabled={working || previewing || !preview} onClick={solana.address ? execute : () => open({ view: 'Connect', namespace: 'solana' })}>{working ? <RefreshCw className="spin" /> : <Wallet />}{solana.address ? 'Review and swap' : 'Connect Solana wallet'}</button>
          : <button className="primary full trade-submit" disabled><Wallet />Swap temporarily unavailable</button>}

        {message && <div className="trade-message">{message}</div>}

        <details className="trade-disclosure inline">
          <summary><span><b>Advanced details</b><small>Rate, minimum and selected assets</small></span><ChevronDown /></summary>
          <div className="quote-summary compact">
            <QuoteRow label="Rate" value={rate} />
            <QuoteRow label="Price impact" value={preview?.priceImpactPct != null ? `${preview.priceImpactPct}%` : '—'} positive={Number(preview?.priceImpactPct || 0) < 1} />
            <QuoteRow label="Minimum received" value={minimum} />
            <TokenDetailRow label="Pay" token={input} price={prices[input.address]?.usdPrice} />
            <TokenDetailRow label="Receive" token={output} price={prices[output.address]?.usdPrice} />
          </div>
        </details>

        {record && <div className="compact-tracking">
          <span><CheckCircle2 /><b>{record.providerStatus || record.status || 'Submitted'}</b></span>
          <code>{short(record.signature || record.sourceTxHash || record.source_tx_hash || '')}</code>
          {String(record.status || '').toLowerCase() === 'pending' && <button className="secondary" disabled={working} onClick={refreshStatus}><RefreshCw />Refresh</button>}
        </div>}
      </section>
    </div>

    {selector && <TokenModal selected={selector === 'input' ? input : output} onSelect={token => choose(selector, token)} onClose={() => setSelector(null)} />}
    <SwapSuccess record={record} onClose={() => setRecord(null)} onRefresh={refreshStatus} />
  </div>
}
